import { patLogs } from "../context/utils";

// Shared hardening for camera previews and for the remote images the capture
// screens depend on.
//
// Playback: every capture screen attaches a getUserMedia stream to a <video>
// and relies on the autoPlay attribute alone. A host WebView that gates
// playback behind a user gesture - the WKWebView default unless the embedder
// clears mediaTypesRequiringUserActionForPlayback - rejects that autoplay
// silently: the preview sits on a black frame, we surface no error, and the
// driver has nothing to tap. Explicit play() plus a tap-to-start overlay turns
// that dead screen into one extra tap.
//
// Preload: guide images come from the persona config in S3 and are otherwise
// first requested at the moment the capture screen renders. Warming them while
// the loading screen is still up is what makes them survive weak signal.

const OVERLAY_TEXT = "Tap to start the camera";

let overlay = null;
let overlayRetry = null;
let overlayOwner = null;

const translate = (config) => {
  try {
    const dict = config && config["language"];
    if (dict && Object.keys(dict).includes(OVERLAY_TEXT)) {
      return dict[OVERLAY_TEXT];
    }
  } catch (err) {
    // fall through to English
  }
  return OVERLAY_TEXT;
};

const removeOverlay = () => {
  if (overlay && overlay.parentNode) {
    overlay.parentNode.removeChild(overlay);
  }
  overlay = null;
  overlayRetry = null;
  overlayOwner = null;
};

// Built in plain DOM rather than per-component JSX so that one implementation
// covers every capture module.
const showOverlay = (config, retry, owner) => {
  overlayRetry = retry;
  overlayOwner = owner;
  if (overlay) return;

  overlay = document.createElement("div");
  overlay.setAttribute("data-tap-to-start", "");
  overlay.style.cssText = [
    "position:fixed",
    "top:0",
    "left:0",
    "width:100%",
    "height:100%",
    "display:flex",
    "flex-direction:column",
    "align-items:center",
    "justify-content:center",
    "gap:16px",
    "background:rgba(0,0,0,0.72)",
    "color:#ffffff",
    "font-size:18px",
    "text-align:center",
    "padding:24px",
    "box-sizing:border-box",
    "cursor:pointer",
    "z-index:2147483000",
  ].join(";");

  const glyph = document.createElement("div");
  glyph.style.cssText = [
    "width:72px",
    "height:72px",
    "border-radius:50%",
    "border:3px solid #ffffff",
    "display:flex",
    "align-items:center",
    "justify-content:center",
  ].join(";");
  glyph.innerHTML =
    '<svg width="28" height="32" viewBox="0 0 28 32" aria-hidden="true">' +
    '<path d="M3 2l22 14L3 30z" fill="#ffffff"/>' +
    "</svg>";

  const label = document.createElement("div");
  label.textContent = translate(config);

  overlay.appendChild(glyph);
  overlay.appendChild(label);

  overlay.addEventListener("click", () => {
    const attempt = overlayRetry;
    if (attempt) attempt();
  });

  document.body.appendChild(overlay);
};

// Attach after `videoEl.srcObject = stream`. `config` is only used to translate
// the overlay copy and may be omitted.
export const startVideoPlayback = (videoEl, config) => {
  if (!videoEl) return;

  // A capture screen the driver left while its overlay was still up must not
  // leave that overlay sitting on top of the next screen.
  if (overlay && overlayOwner !== videoEl) removeOverlay();

  try {
    // Set on the element rather than through the JSX `muted` prop, which React
    // applies unreliably. Safe here: these are live getUserMedia previews, and
    // MediaRecorder reads the stream, not the element, so recording is
    // unaffected. An unmuted element is the most common autoplay blocker.
    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.setAttribute("muted", "");
    videoEl.setAttribute("playsinline", "");
    videoEl.setAttribute("webkit-playsinline", "");
    if (!videoEl.dataset || !videoEl.dataset.playbackBound) {
      // "emptied" fires when the stream is detached on teardown - drop the
      // overlay then too, so it cannot outlive the element it belongs to.
      videoEl.addEventListener("emptied", () => {
        if (overlayOwner === videoEl) removeOverlay();
      });
      if (videoEl.dataset) videoEl.dataset.playbackBound = "1";
    }
  } catch (err) {
    // Non-fatal - autoPlay in the markup is still in play.
  }

  // play() returns undefined on older engines - there we have no rejection to
  // react to, so treat the call itself as success.
  const attempt = () => {
    try {
      const played = videoEl.play();
      if (!played || typeof played.then !== "function") {
        removeOverlay();
        return Promise.resolve();
      }
      return played.then(removeOverlay);
    } catch (err) {
      return Promise.reject(err);
    }
  };

  attempt().catch((err) => {
    patLogs(
      "autoplay blocked by host, showing tap-to-start: " +
        ((err && err.name) || err),
    );
    showOverlay(
      config,
      () => {
        attempt().catch((retryErr) => {
          // Still blocked even from inside a gesture - leave the overlay up so
          // the driver can try again rather than dropping them on a black frame.
          patLogs(
            "tap-to-start retry failed: " +
              ((retryErr && retryErr.name) || retryErr),
          );
        });
      },
      videoEl,
    );
  });
};

// Fire-and-forget warming of every guide image referenced by the config.
// Fully guarded: a malformed or partial config must be a no-op, never a throw
// on the loading path.
export const preloadConfigImages = (config) => {
  try {
    const urls = [];
    const push = (value) => {
      if (typeof value === "string") urls.push(value);
    };
    const pushFrom = (obj, keys) => {
      if (!obj) return;
      keys.forEach((key) => push(obj[key]));
    };

    const blocks = (config && config["blocks"]) || [];
    blocks.forEach((block) => {
      if (!block) return;
      // block.link is the module icon on the menu; values carry the per-position
      // guide tile plus the stencil and shadowbox drawn over the live camera.
      pushFrom(block, ["link"]);
      const values = block["values"] || [];
      values.forEach((value) => {
        pushFrom(value, ["image", "stencils", "shadowBox"]);
      });
    });

    pushFrom(config, ["logo"]);
    pushFrom(config && config["intro-page"], ["image"]);
    pushFrom(config && config["success-page"], ["image"]);
    pushFrom(config && config["single-image"], [
      "kiwi-odo-img",
      "selfi_inst_refImg",
    ]);
    pushFrom(config && config["damage-page"], [
      "zoomIn-menu-image",
      "zoomOut-menu-image",
    ]);
    pushFrom(config && config["windshieldDamage"], [
      "imageOne",
      "imageTwo",
      "imageThree",
    ]);

    urls
      // Empty strings are the guaranteed-broken case SafeImage already guards;
      // data URIs are inline and have nothing to warm.
      .filter((url) => url && url.indexOf("data:") !== 0)
      .forEach((url) => {
        const img = new Image();
        img.src = url;
      });
  } catch (err) {
    console.log("guide image preload skipped", err);
  }
};
